import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import {
    AlarmList,
    ApprovalStatus,
    ComponentDefinition,
    Dashboard,
    DataToApprove,
    Dictionary,
    DigitalSet,
    DigitalSetSimpleDictionary,
    EditSpotValueWithOptions,
    Group,
    GroupConfigMode,
    IApplicationConfiguration,
    IApproval,
    IApprovalButtonsConfiguration,
    IApprovalStrategy,
    IFrequency,
    IGridsterOptions,
    IIdentifierVm,
    IValueViewModel,
    Interval,
    Miniature,
    RefreshClickData,
    SpotValue,
    TagDefinition,
    TagManualInputValues,
    TagStringValueDto,
    ValidationPageConfiguration,
    ValidationScope,
    WidgetClass
} from '../../../models';
import {
    IBindableWidgetConfiguration,
    IMaximizable,
    IValidatedValueSelectionConfiguration,
    IWidget,
    IWidgetControl,
    IWidgetFactory,
    IWidgetLayout,
    IZoomableWithLogbookSelectionWidget
} from '../../../widgets';
import { GridsterOptions } from '../../gridsterOptions';
import {
    GroupConfig,
    IAuthorizationService,
    IConfigurationService,
    ICurrentApplicationService,
    IDashboardHelperService,
    IDateRangeService,
    IGroupApprovalService,
    IGroupEnforceAlignService,
    IGroupSelectionService,
    IIntervalService,
    ILogbookModalSeries,
    ILogbookService,
    ILoggerService,
    INavigationHistoryService,
    ISpotValueHelperService,
    ISpotValuesService,
    IStringValuesService,
    IUserSettingsService,
    IValidationPageConfigurationService,
    IWidgetDuplicationService
} from '../../../services';
import { Roles, editGroupWidgetShapeState, editGroupWidgetState, guidEmpty, guidGroupValidationPageLink, momentFormat } from '../../../common/constants';
import { formDirtyChanged, groupSelected, refreshClick, toggleEditMode, toggleMaximizeWidget, widgetDragged, widgetResized } from '../../../common/events';
import { IRootScope } from '../../../IRootScope';
import { IValidationStateService, LoadObject } from './validationState.service';
import { ValidatedValueSelectionWidget } from '../../../widgets/validatedValueSelection/validatedValueSelection.widget';
import { ManualInputsWidget } from '../../../widgets/manualInputs/manualInputs.widget';
import { ManualInputWidgetProperties } from '../../../widgets/manualInputs/extraProperties';
import { IManualInputsWidgetsService } from './manualInputsWidgets.service';
import { IApprovalManagementService } from './approvalManagement.service';
import { ValidationStateStoreMap } from './validationStateStoreMap';
import { widgetName, manualInputsWidgetType, spotManualInputsWidgetType, specialWidgetTypes } from './validation.constants';
import { ManualInputsWidgetConfiguration } from '../../../widgets/manualInputs/manualInputs.configuration';
import { ValidationStateDispatchMap } from './validationStateDispatchMap';
import { ILogbookSelection, IZoomControl } from '../../../widgets/models/interfaces';
import { NavigationMode } from '../navigationMode';
import { getWidgetClass } from '../../../common/helpers/getWidgetClass';
import { LogbookTableWidget, SpotManualInputsWidget } from '../../../widgets/widgets.index';
import { maxRows } from '../../dashboard/dashboardConstants';
import { getValidationPageTitle } from './getValidationPageTitle';
import { renamableWidgets } from '../../../widgets/widgetTypes';
import { MaxColumns } from '@common/helpers/contextual';

declare type ManualInputsWidgetWithExtraProperties = ManualInputsWidget & ManualInputWidgetProperties;

export const VALIDATION_DATE = 'VALIDATION_DATE';

interface IValidationStateShare {
    approvalConfiguration: IApprovalButtonsConfiguration;
    cancelWidgets: () => angular.IPromise<void>;
    formController: angular.IFormController;
    hide: boolean;
    isDirty: boolean;
    saveWidgets: () => angular.IPromise<void>;
}

interface ILoadAndMapResponse {
    groupConfig: GroupConfig;
    widgets: IWidget[];
    groupsWithWidgets: Group[];
}

class ValidationStateController implements angular.IComponentController, ValidationStateStoreMap, ValidationStateDispatchMap {
    alarmList: AlarmList;
    application: IApplicationConfiguration;
    approvalsToSave: DataToApprove[];
    approvalStrategies: Dictionary<IApprovalStrategy> = {};
    baseFrequency: IFrequency;
    canRename: boolean;
    canValidate = false;
    currentInterval: any;
    dashboardNames: Dictionary<string>;
    dashboards: Dictionary<Dashboard>;
    dashboardsList: Dashboard[] = [];
    date: string;
    dateKeyList: string[];
    datesByGroup: Dictionary<Date[]>;
    digitalSetDictionary: Dictionary<DigitalSet>;
    digitalSets: DigitalSetSimpleDictionary;
    endDate: string;
    // Dictionary of frequency by widget.
    frequencies: { [widgetGuid: string]: IFrequency };
    frequency: IFrequency;
    // Promise from the modal.
    getApprovalJustification: (isRejection: boolean, groupName: string, date: string) => angular.IPromise<string>;
    gridsterOptions: IGridsterOptions;
    groups: Group[];
    groupConfigs: Dictionary<IApprovalButtonsConfiguration>;
    groupsDictionary: Dictionary<Group>;
    groupsWithVP: Group[];
    hasApprobation: boolean;
    hasManualInputRoles: boolean;
    hover: Dictionary<boolean>;
    interval: Interval;
    isApprovalButtonsLoaded = false;
    isEditMode: boolean;
    isGroupApproved: Dictionary<{ isApproved: boolean; label?: string }>;
    isInputWidget: boolean;
    isLoaded = false;
    isLoading = false;
    isLoadingData: boolean;
    isOutsideWindow: boolean = false;
    isSavingSpotValues: boolean = false;
    loadingMessageText: string = 'Loading';
    manualInputsWidgetType = manualInputsWidgetType;
    manualInputTagsPerGroup: Dictionary<TagManualInputValues[]>;
    maximized: string;
    miniatureList: Miniature[];
    pickerDisabled: boolean;
    referenceGroup?: string;
    selectedDate: Date;
    selectedItem: IWidget;
    showButtons: { [guid: string]: boolean };
    spotManualInputsWidgetType = spotManualInputsWidgetType;
    spotManualTagsPerGroup: Dictionary<TagDefinition[]>;
    spotManualTagsPerWidget: Dictionary<TagDefinition[]>;
    spotValues: Dictionary<EditSpotValueWithOptions[]>;
    stringValues: TagStringValueDto[];
    // for standAlone
    title: string;
    // for store
    update: (type: string, referenceGroup: string, widgets: IWidget[]) => angular.IPromise<ValidationPageConfiguration[]>;
    validationPageConfigurations: Dictionary<ValidationPageConfiguration>;
    viewModels: Dictionary<IValueViewModel>;
    // Dictionary providing the group for each manual or validation inputs widget
    widgetGroups: Dictionary<Group>;
    widgetList: IWidget[];
    widgetName = widgetName;

    readonly navigationMode: NavigationMode;
    readonly selectedGroup: Group;
    readonly share: IValidationStateShare;

    private _crosshairPropagationGarbages: (() => void)[] = [];
    private _garbages: (() => void)[] = [];
    private _hasDestroyed = false;
    private _pristineGroupsConfig: Dictionary<IApprovalButtonsConfiguration>;
    private _pristineWidgets: Dictionary<IWidgetLayout>;
    private _unsubscribe: Function;

    static $inject = [
        '$q',
        '$scope',
        '$rootScope',
        '$state',
        '$translate',
        '$timeout',
        '$window',
        'intervalService',
        'groupApprovalService',
        'dateRangeService',
        'groupSelectionService',
        'widgetFactory',
        'validationPageConfigurationService',
        'authorizationService',
        'loggerService',
        'approvalManagementService',
        'currentApplicationService',
        'navigationHistoryService',
        'userSettingsService',
        'manualInputsWidgetsService',
        'validationStateService',
        'dashboardHelperService',
        'groupEnforceAlignService',
        'configurationService',
        'logbookService',
        'spotValuesService',
        'spotValueHelperService',
        'stringValuesService',
        'widgetDuplicationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: angular.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _$timeout: angular.ITimeoutService,
        private _$window: angular.IWindowService,
        private _intervalService: IIntervalService,
        private _groupApprovalService: IGroupApprovalService,
        private _dateRangeService: IDateRangeService,
        private _groupSelectionService: IGroupSelectionService,
        private _widgetFactory: IWidgetFactory,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _authorizationService: IAuthorizationService,
        private _loggerService: ILoggerService,
        private _approvalManagementService: IApprovalManagementService,
        private _currentApplicationService: ICurrentApplicationService,
        private _navigationHistoryService: INavigationHistoryService,
        private _userSettingsService: IUserSettingsService,
        private _manualInputsWidgetsService: IManualInputsWidgetsService,
        private _validationStateService: IValidationStateService,
        private _dashboardHelperService: IDashboardHelperService,
        private _enforceAlignService: IGroupEnforceAlignService,
        private _configurationService: IConfigurationService,
        private _logbookService: ILogbookService,
        private _spotValuesService: ISpotValuesService,
        private _spotValuesHelperService: ISpotValueHelperService,
        private _stringValuesService: IStringValuesService,
        private _widgetDuplicationService: IWidgetDuplicationService
    ) {}

    $onInit(): void {
        this.hasManualInputRoles = this._authorizationService.isInRoles(Roles.manualInputRoles);
        this.approvalsToSave = [];
        this.datesByGroup = {};
        this.frequencies = {};
        this.isGroupApproved = {};
        this.groupConfigs = {};
        this._pristineGroupsConfig = {};
        this.widgetList = [];
        this.hover = {};
        this.share.saveWidgets = () => this.saveWidgets();
        this.share.cancelWidgets = () => this.cancelWidgets();
        this.gridsterOptions = new GridsterOptions().defaultWidgetConfigure(this._$rootScope, (value?) => {
            if (!!value) {
                this.setSelectedItem(value);
            }
            return this.selectedItem;
        });
        this.configureGridster(this._$rootScope);
        this.setEditMode(this._$rootScope.isEditMode);

        // On toggle edit mode
        this._$scope.$on(toggleEditMode, () => this.setEditMode(this._$rootScope.isEditMode));

        // On form changed
        this._$scope.$on(formDirtyChanged, () => {
            const validationWidgets = this.getValidationsWidgets();
            this.share.isDirty = _.some(validationWidgets, (w: ValidatedValueSelectionWidget) => w.ctrl?.isDirty);
        });
        this.initMiniatureList();
    }

    $onChanges(changesObj: angular.IOnChangesObject): void {
        if (changesObj['selectedGroup']?.currentValue) {
            const app = this._currentApplicationService.getApplicationConfiguration();
            this.baseFrequency = {
                interval: app.interval,
                intervalTimeUnit: app.intervalTimeUnit
            };
            this.hasApprobation = app ? app.validationEnabled : false;
            this.canValidate = this._authorizationService.isInRoles(Roles.validationRoles);
            this.application = app;
            if (
                !changesObj['selectedGroup']?.previousValue ||
                (changesObj['selectedGroup'].previousValue as Group).guid !== (changesObj['selectedGroup'].currentValue as Group).guid
            ) {
                this.initialize();
                this._$state.go(this._$state.current.name, Object.assign({}, this._$state.current.params, { groupGuid: this.selectedGroup?.guid }), {
                    reload: false
                });
            }
        }
    }

    $onDestroy(): void {
        this._garbages.forEach((g) => g());
        if (this._crosshairPropagationGarbages) {
            this._crosshairPropagationGarbages.forEach((c) => c());
        }
        this._unsubscribe();
        this._hasDestroyed = true;
    }

    private initMiniatureList(): void {
        const widgetsMiniature = this._widgetFactory.widgetList().filter((m) => {
            if (!this._configurationService.configuration.useExperimentalFeatures) {
                return !m.isExperimental;
            } else {
                return true;
            }
        });
        this.miniatureList = widgetsMiniature;
    }

    //#region Initial load
    private initialize(): void {
        this.initializeGroup();
        if (this.navigationMode === NavigationMode.Single) {
            this.initializeSinglePeriod();
        }
        this.title = getValidationPageTitle(this.selectedGroup);
        this.updateHistoryName(this.selectedGroup.name);
        const { unsubscribe, firstLoad } = this._validationStateService.connect(this, this.selectedGroup, this.baseFrequency);
        this._unsubscribe = unsubscribe;
        this.initialLoad(firstLoad);
    }

    private initialLoad(loadPromise: angular.IPromise<void>, addWatchers = true): angular.IPromise<void> {
        this.isLoading = true;
        const promises = {
            groupConfig: this._groupApprovalService.getGroupConfig(this.selectedGroup?.guid),
            storeItems: loadPromise,
            widgets: loadPromise.then(() => this._validationPageConfigurationService.get(this.selectedGroup?.guid, this.viewModels, this.groupsDictionary)),
            groupsWithWidgets: this._validationPageConfigurationService.getGroupsWithWidgets()
        };
        return this._$q
            .all(promises)
            .then((responses) => this.loadAndMap(responses))
            .then((responses) => {
                if (this.dashboards) {
                    this.dashboardsList = Object.values(this.dashboards);
                }
                this.groupsWithVP = (responses.groupsWithWidgets as Group[]).sort((a, b) => (a.uniqueName > b.uniqueName ? 1 : -1));
                const buttonsConfigurations = {};
                for (const g of Object.keys(responses.groupConfig)) {
                    const group = this.groupsDictionary[g];
                    const config = responses.groupConfig[g];
                    const buttonConfiguration = this.buildGroupApprovalConfig(config, group);
                    buttonsConfigurations[g] = buttonConfiguration;
                }
                this.groupConfigs = buttonsConfigurations;
                this._pristineGroupsConfig = angular.copy(this.groupConfigs);
                this.isApprovalButtonsLoaded = true;
                this.checkIntegrity();
                this._$rootScope.$broadcast(groupSelected, this.selectedGroup);
                return this.updateDate();
            })
            .then(() => {
                if (addWatchers) {
                    this._$scope.$watch(
                        () => this.selectedDate,
                        () => this.onSelectedSingleDateChange()
                    );
                    this._$scope.$on(refreshClick, (_event, data: RefreshClickData) => this.onRefreshClick(data));
                }
            })
            .catch((error) => this._loggerService.logError(error))
            .finally(() => {
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    private onRefreshClick(data: RefreshClickData): void {
        this.updateDate(data);
    }

    private onSelectedSingleDateChange(): void {
        const endDate = moment(this.selectedDate).add(this.interval.interval, this.interval.unit).toISOString();
        this.endDate = endDate;
        this.currentInterval = { startDate: this.selectedDate, endDate: endDate };
        this._userSettingsService.setApplicationSetting(VALIDATION_DATE, this.selectedDate);
    }

    private loadAndMap(responses: ILoadAndMapResponse): angular.IPromise<any> {
        const { groupConfig, widgets, groupsWithWidgets } = responses;
        const { widgetGroups, frequencies, groupConfigPromises, config } = this.loadInitialObjects(widgets, groupConfig);
        if (this.dashboards) {
            this.dashboardNames = this._dashboardHelperService.getDashboardNames(this.dashboards);
        }
        this.widgetGroups = widgetGroups;
        this.frequencies = frequencies;
        const lp = widgets.map((w) => {
            return this._$q.when(() => {
                (w as any).hasApprovalStrategy = this.hasApprovalStrategy(w);
                if (w.type === widgetName) {
                    (w as any).isIncorrectInterval = this.isIncorrectInterval(w);
                } else if (w.type === manualInputsWidgetType) {
                    return this.mapManualInputWidgets(w as ManualInputsWidgetWithExtraProperties);
                }
            });
        });
        return this._$q.all(lp).then(() => {
            this._crosshairPropagationGarbages = this.registerCrosshairPropagationList(widgets);
            this.widgetList = widgets;
            this.checkMaximized();
            this.computeShowButtons();
            this.computeLinkIcons();
            this.saveRestoreState(this.widgetList);
            this.share.approvalConfiguration = config;
            const promises = {
                groupConfig: this._$q.all(groupConfigPromises),
                groupsWithWidgets: this._$q.when(groupsWithWidgets)
            };
            return this._$q.all(promises);
        });
    }

    private loadInitialObjects(widgets: any[], groupConfig: GroupConfig): LoadObject {
        return this._validationStateService.loadInitialObjects(
            widgets,
            groupConfig,
            this.referenceGroup,
            this.selectedGroup,
            this.groupsDictionary,
            this.getBeginDate(),
            this.interval,
            (level, status, dateToApprove?) => this.updateApprove(this.selectedGroup?.guid, level, status, dateToApprove ? dateToApprove : this.getBeginDate())
        );
    }

    private isIncorrectInterval(widget: IWidget): boolean {
        let comparedFrequency = this.frequencies[widget.guid];
        if (!comparedFrequency || !comparedFrequency.interval) {
            comparedFrequency = this.baseFrequency;
        }
        const isValidation = widget.type === widgetName;
        return (
            isValidation &&
            !!(widget.configuration as IValidatedValueSelectionConfiguration).group && // if no group specified, current group => always ok
            widget.guid !== guidEmpty &&
            comparedFrequency &&
            (comparedFrequency.interval !== this.frequency.interval || comparedFrequency.intervalTimeUnit !== this.frequency.intervalTimeUnit)
        );
    }

    private buildGroupApprovalConfig(groupConfig: GroupConfig, group: Group): IApprovalButtonsConfiguration {
        return this.buildGenericApprovalConfig(groupConfig, group, undefined, this.getInterval(group), true);
    }

    private buildGenericApprovalConfig(
        config: GroupConfig,
        group: Group,
        selectedDate: Date,
        interval: Interval,
        inWidget: boolean
    ): IApprovalButtonsConfiguration {
        return this._validationStateService.buildGenericApprovalConfig(config, selectedDate, interval, inWidget, (level, status, dateToApprove?) =>
            this.updateApprove(group.guid, level, status, dateToApprove ? dateToApprove : this.getBeginDate())
        );
    }

    private checkIntegrity(): void {
        if (this.selectedGroup?.configMode === GroupConfigMode.ByType && this.referenceGroup === null) {
            this._loggerService.logWarning('Your page has no reference group and must be configured.');
        }
    }
    //#endregion

    private initializeSinglePeriod(): void {
        let dateParam: string = this._$state.params['beginDate'];
        if (dateParam) {
            this._userSettingsService.setApplicationSetting(VALIDATION_DATE, this.selectedDate);
        } else {
            dateParam = this._userSettingsService.getApplicationSetting(VALIDATION_DATE);
        }
        if (!dateParam) {
            dateParam = this.getBegin();
        }
        if (dateParam) {
            const isSinglePeriod = this.selectedGroup?.validationScope !== ValidationScope.MultiplePeriod;
            const alignedRange = this._enforceAlignService.initializeSinglePeriod(dateParam, this.frequency, this.application, this.interval, isSinglePeriod);
            const format = momentFormat;
            this.selectedDate = alignedRange.startDate.toDate();
            this.date = alignedRange.startDate.format(format);
            const endDate = alignedRange.endDate.toISOString();
            this.endDate = endDate;
        }
    }

    private checkMaximized(): void {
        this.maximized = this._$state.params.maximized;
        if (this.widgetList && this.maximized && this.maximized !== 'false') {
            const maximizedWidget = this.widgetList.filter((w) => w.guid === this.maximized);
            if (maximizedWidget && maximizedWidget.length > 0 && !(maximizedWidget[0] as IWidget & IMaximizable).isDisabledMaximized) {
                (maximizedWidget[0] as IWidget & IMaximizable).isMaximized = true;
                this.isOutsideWindow = true;
                this._$rootScope.$broadcast(toggleMaximizeWidget, { isMaximized: (maximizedWidget[0] as IWidget & IMaximizable).isMaximized });
            }
        }
    }

    //#region gridster

    // Gridster configuration
    private configureGridster($rootScope: angular.IRootScopeService): void {
        this.gridsterOptions.rowHeight = 20;
        this.gridsterOptions.maxRows = maxRows;
        this.gridsterOptions.minRows = 36;
        this.gridsterOptions.sparse = true;
        this.resizeGridster();

        let startCol: number;
        let startRow: number;
        this.gridsterOptions.draggable.start = (event, _uiWidget, $element: IWidget) => {
            if (!this.share.formController || !this.share.formController.$dirty) {
                if (!this.selectedItem || this.selectedItem !== $element) {
                    this.select($element, event);
                }
            }
            startCol = $element.col;
            startRow = $element.row;
        };

        this.gridsterOptions.draggable.stop = (_event, _uiWidget, $element) => {
            if (startCol !== $element.col || startRow !== $element.row) {
                if (this.validateSize(this.gridsterOptions.maxRows, this.gridsterOptions.columns)) {
                    $rootScope.$broadcast(widgetDragged, {
                        col: $element.col,
                        row: $element.row,
                        guid: $element.guid
                    });
                    startCol = null;
                    startRow = null;
                } else {
                    $element.col = startCol;
                    $element.row = startRow;
                }
            }
        };

        let startSizeX: number;
        let startSizeY: number;
        this.gridsterOptions.resizable.start = (event, _uiWidget, $element: IWidget) => {
            this.resizeGridster();
            if (!this.selectedItem || this.selectedItem !== $element) {
                this.select($element, event);
            }
            startSizeX = $element.sizeX;
            startSizeY = $element.sizeY;
        };

        this.gridsterOptions.resizable.stop = (_event, _uiWidget, $element) => {
            if (startSizeX !== $element.sizeX || startSizeY !== $element.sizeY) {
                if (this.validateSize(this.gridsterOptions.maxRows, this.gridsterOptions.columns)) {
                    $rootScope.$broadcast(widgetResized, {
                        sizeX: $element.sizeX,
                        sizeY: $element.sizeY,
                        guid: $element.guid
                    });
                    startSizeX = null;
                    startSizeY = null;
                } else {
                    $element.sizeX = startSizeX;
                    $element.sizeY = startSizeY;
                }
            }
        };
    }

    private validateSize(currentMaxRows: number, currentMaxCols: number): boolean {
        return !_.some(this.widgetList, (w) => w.row + w.sizeY > currentMaxRows || w.col + w.sizeX > currentMaxCols);
    }

    private resizeGridster(): void {
        this.gridsterOptions.margins = this._$window.innerWidth < 1500 ? [8, 8] : [14, 14];
    }
    //#endregion gridster

    //#region Widgets
    private saveWidgets(): angular.IPromise<any> {
        this.isLoading = true;
        this.loadingMessageText = 'Saving widgets';

        const promise: angular.IPromise<any> =
            this.selectedGroup?.configMode === GroupConfigMode.ByType
                ? this.update(this.selectedGroup.type, this.referenceGroup, this.widgetList)
                : this._validationPageConfigurationService.update(this.selectedGroup?.guid, this.widgetList);

        return promise
            .then(() => {
                if (this.share.formController && this.share.formController.$setPristine) {
                    this.share.formController.$setPristine();
                }
                this._loggerService.logSuccess('Group configuration saved');
                // refresh everything as widgets have changed. Not very efficient, but at least correct
                return this.initialLoad(this._$q.when(), false).then(() => {
                    if (this.selectedItem) {
                        // reselect or deselect widget to ensure we edit the good one
                        this.selectedItem = _.find(this.widgetList, (w) => w.guid === this.selectedItem.guid);
                    }
                });
            })
            .finally(() => {
                this.loadingMessageText = null;
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    private cancelWidgets(): angular.IPromise<void> {
        this.select(null, null);
        this.widgetList.forEach((widget) => {
            const pristine = this._pristineWidgets[widget.guid];
            if (pristine) {
                for (const key of Object.keys(pristine)) {
                    widget[key] = pristine[key];
                }
            }
        });
        return this._$q.resolve();
    }

    select(item: IWidget, $event: JQuery.Event): void {
        if (this.isEditMode) {
            if (this.selectedItem !== item) {
                if (this.share.formController && this.share.formController.$dirty) {
                    this._loggerService.logWarning('Save or cancel changes before selecting another widget');
                } else {
                    this.setSelectedItem(item);
                }
            }
            if ($event && $event.preventDefault) {
                $event.preventDefault();
            }
        }
    }

    private setSelectedItem(item: IWidget): void {
        this.selectedItem = item;
    }

    unselect($event: JQuery.Event): void {
        if (!$event.isDefaultPrevented()) {
            this.select(null, $event);
        }
    }

    drop(item: { itemId: string }): void {
        if (item && item.itemId) {
            const widget = this._widgetFactory.createWidget(item.itemId);
            if (widget) {
                if (widget.sanitize) {
                    widget.sanitize();
                }
                widget.isTitleHidden = false;
                // add widget to bottom
                widget.row = this.widgetList.length ? Math.max(...this.widgetList.map((w) => w.row + w.sizeY)) : 0;
                this.widgetList.push(widget);
                this.setSelectedItem(widget);
                this.saveWidgets();
                this.registerCrosshairPropagationList(this.widgetList);
            }
        }
    }

    deleteWidget(guid: string): void {
        const idx = _.findIndex(this.widgetList, (w) => w.guid === guid);
        if (idx >= 0) {
            this.widgetList.splice(idx, 1);
        }
        if (this.selectedItem.guid === guid) {
            this.setSelectedItem(null);
        }

        this.saveWidgets()
            .then(() => {
                this.isLoading = true;
                return this._validationStateService.postDeleteWidget();
            })
            .finally(() => {
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    duplicateWidget(guid: string): void {
        const original = _.find(this.widgetList, (w) => w.guid === guid);
        if (original) {
            this.isInputWidget = specialWidgetTypes.indexOf(original.type) >= 0;
            this.canRename = renamableWidgets.indexOf(original.type) >= 0;
            $('#duplicateWidgetModal').modal('show');
        }
    }

    addWidgetToPage(groupGuid: string, widget: IWidget) {
        this.isLoading = true;
        return this._widgetDuplicationService
            .addWidgetToPage(groupGuid, widget, this.groupsDictionary, this.validationPageConfigurations, this.viewModels, this.update)
            .then(() => this.initialLoad(this._$q.when(), false))
            .then(() => {
                if (this.selectedItem) {
                    // reselect or deselect widget to ensure we edit the good one
                    this.selectedItem = _.find(this.widgetList, (w) => w.guid === this.selectedItem.guid);
                }
            })
            .finally(() => {
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    // TODO duplicate code from wtWidgetList
    exportToCsv(item: any): void {
        this.isLoading = true;
        this.loadingMessageText = 'Exporting to csv';
        try {
            this.setSelectedItem(item);
            item.exportToCsv(this.selectedGroup?.guid, item.data)
                .catch((error: Error) => this._loggerService.logError(error.message))
                .finally(() => {
                    this.isLoading = false;
                    this.pickerDisabled = false;
                    this.loadingMessageText = null;
                });
        } catch (e) {
            this.isLoading = false;
            this.pickerDisabled = false;
            this.loadingMessageText = null;
        }
    }

    getClass(item: IWidget & { isIncorrectInterval?: boolean; hasValidationError?: boolean }): WidgetClass {
        const result = getWidgetClass(item);
        result['bordered'] = this.selectedItem === item;
        result['panel-warning'] = result['panel-warning'] || item.isIncorrectInterval;
        result['panel-error'] = result['panel-error'] || item.hasValidationError;
        return result;
    }
    //#endregion

    //#region UI interactions
    private setEditMode(isEditMode: boolean) {
        this.isEditMode = isEditMode;
        this.gridsterOptions.draggable.enabled = this.isEditMode;
        this.gridsterOptions.resizable.enabled = this.isEditMode;
        if (!this.isEditMode) {
            this.setSelectedItem(null);
        }
        this.computeShowButtons();
        this.disableLogbookSelection();
    }

    private registerCrosshairPropagationList(widgetList: IWidget[]) {
        const types = ['tagsTrend', 'GroupStates', 'scatter'];
        return widgetList
            .filter((w) => !!w && !!w.configuration && !!w.configuration.options && types.indexOf(w.type) >= 0)
            .map((w) => {
                return this._$scope.$watch(
                    () => w.configuration.options.crosshairDate,
                    (newValue: any, oldValue: any) => {
                        if (newValue !== oldValue) {
                            this.crosshairPropagate(newValue);
                        }
                    }
                );
            });
    }

    private crosshairPropagate(date: Date): void {
        this.widgetList.forEach((w) => {
            if (w && w.configuration && (w.configuration as any).options) {
                (w.configuration as any).options.crosshairDate = date;
            }
        });
    }

    isApproved(item: ValidatedValueSelectionWidget): boolean {
        const status = this.getStatus(item);
        return status ? status.isApproved : false;
    }

    approvedLabel(item: ValidatedValueSelectionWidget): string {
        const status = this.getStatus(item);
        return status ? status.label : '';
    }

    private getStatus(item: ValidatedValueSelectionWidget) {
        let groupGuid = item.configuration.group;
        if (item.configuration.isBound && this.widgetGroups && this.widgetGroups[item.guid]) {
            groupGuid = this.widgetGroups[item.guid].guid;
        }
        const status = (groupGuid && this.isGroupApproved[groupGuid]) || this.isGroupApproved[this.selectedGroup?.guid];
        return status;
    }

    showDashboardLink(guid: string): boolean {
        return guid && guid !== guidEmpty;
    }

    saveAndForceRefreshAll(ctrl: IWidgetControl): void {
        if (!ctrl.editingManual) {
            this._$timeout(() => this.saveAndForceRefreshAllSync());
        }
    }

    private saveAndForceRefreshAllSync(): void {
        const manualToSave = this.getManualWidgets()
            .filter((w) => w.ctrl.form.$dirty && w.ctrl.form.$valid)
            .map((w) => w.ctrl.save());

        const validationToSave = this.getValidationsWidgets()
            .filter((w) => w.ctrl.isDirty && w.ctrl.form.$valid)
            .map((w) => w.ctrl.save());

        this._$q.all(validationToSave.concat(manualToSave)).then(() => {
            const refreshData: RefreshClickData = {
                beginDate: this.getBegin(),
                endDate: this.getEnd(),
                force: true
            };
            this._$rootScope.$broadcast(refreshClick, refreshData);
        });

        if (this.approvalsToSave && this.approvalsToSave.length > 0) {
            this.approveData(this.approvalsToSave, false);
            this.approvalsToSave = [];
        }
    }

    cancelApproval(groupGuid: string): void {
        // Empty array of changes
        this.approvalsToSave = [];
        // Reset approval on UI
        this.groupConfigs[groupGuid] = angular.copy(this._pristineGroupsConfig[groupGuid]);
    }

    //#endregion

    //#region filters

    // Returns the list of ValidatedValueSelectionWidget in the current list of widgets.
    private getValidationsWidgets(): ValidatedValueSelectionWidget[] {
        return this.widgetList.filter((w) => w.type === widgetName) as any as ValidatedValueSelectionWidget[];
    }

    // Returns the list of ManualInputsWidget in the current list of widgets.
    private getManualWidgets(): (ManualInputsWidget & ManualInputsWidgetWithExtraProperties)[] {
        return this.widgetList.filter((w) => w.type === manualInputsWidgetType) as unknown as (ManualInputsWidget & ManualInputsWidgetWithExtraProperties)[];
    }

    // Returns the list of ManualInputsWidget in the current list of widgets.
    private getSpotManualWidgets(): SpotManualInputsWidget[] {
        return this.widgetList.filter((w) => w.type === spotManualInputsWidgetType) as SpotManualInputsWidget[];
    }
    //#endregion

    private getEditDates(): { begin: string; end: string } {
        let beginDate: string;
        let endDate: string;

        if (!this._dateRangeService.initialized()) {
            const now = moment();
            endDate = now.toISOString();
            beginDate = now.add(-1, 'day').toISOString();
        } else {
            beginDate = this.getBegin();
            endDate = this.getEnd();
        }

        return { begin: beginDate, end: endDate };
    }

    private getInterval(group: Group): Interval {
        return this._intervalService.getGroupInterval(group, this.baseFrequency);
    }

    private initializeGroup(): void {
        if (this.selectedGroup) {
            this._groupSelectionService.selectedGroup(this.selectedGroup);
            this.frequency = this.selectedGroup.frequency.interval > 0 ? this.selectedGroup.frequency : this.baseFrequency;
            this.interval = this.getInterval(this.selectedGroup);
        }
    }

    private mapManualInputWidgets(w: ManualInputsWidgetWithExtraProperties): angular.IPromise<void> {
        const { configuration } = w;
        if (configuration.showMissingWarnings === undefined) {
            configuration.showMissingWarnings = true;
        }

        const group = this.widgetGroups[w.guid];
        let promise: angular.IPromise<void>;
        if (group) {
            if (group.approvalStrategyGuid === guidEmpty && configuration.enableApproval) {
                // No approval strategy but approval enabled on widget => Display warning on widget
                (w as any).isInvalidApprovalConfig = true;
            }
            this.datesByGroup[group.guid] = w.dates;
            if (!w.dates) {
                w.dates = [];
            }
            promise = this._validationStateService
                .filterAndSortManualInputs(this.manualInputTagsPerGroup, group, this.groupsDictionary, configuration)
                .then((il) => {
                    w.inputList = il;
                });
        } else {
            promise = this._$q.when();
        }
        return promise;
    }

    private readManualInputs(force: boolean = false): angular.IPromise<ManualInputsWidgetWithExtraProperties[]> {
        const widgets = this.getManualWidgets() as ManualInputsWidgetWithExtraProperties[];

        return widgets.length > 0
            ? this._manualInputsWidgetsService.readManualInputs(widgets, this.application, this.getBeginDate(), this.getEndDate(), this.hasApprobation, force)
            : this._$q.when([]);
    }

    /**
     * Refreshes status of global buttons and manual input data.
     * Validation widgets load their own data...
     * @param force Force the refresh by cleaning cache.
     */
    private refreshLocalData(force: boolean = false): angular.IPromise<ManualInputsWidgetWithExtraProperties[]> {
        this.isLoading = true;
        return this._$q
            .all([this.readManualInputs(force), this.readSpotValues(force)])
            .then((data) => {
                this.spotValues = data[1];
                return data[0];
            })
            .finally(() => {
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    // This is called when updating date from datepicker
    // Also called after saving manual inputs (See manualInputsPanel.component.ts)
    private updateDate(data?: RefreshClickData): angular.IPromise<void> {
        const begin = data?.beginDate && !data?.isLogbookEntryUpdated ? data.beginDate : this.getBegin();
        const selectedBeginDate = moment(begin).toDate();
        const end = data?.endDate && !data?.isLogbookEntryUpdated ? data.endDate : this.getEnd();
        const selectedEndDate = moment(end).toDate();
        const promises: angular.IPromise<any>[] = [this._stringValuesService.getAll()];
        if (this.isSingleDate()) {
            this.selectedDate = selectedBeginDate;
        }
        if (begin && this.selectedGroup) {
            this.isLoadingData = true;
            const otherWidgets = this.widgetList.filter((w) => w.type !== widgetName);
            const end = data?.endDate && !data?.isLogbookEntryUpdated ? data.endDate : this.getEnd();
            const promise = this._validationStateService.refreshAndAssign(
                otherWidgets,
                begin,
                end,
                this.selectedGroup.guid,
                this.digitalSets,
                this.stringValues,
                this.viewModels,
                true
            );
            promises.push(promise);
        }
        return this._$q
            .all(promises)
            .then((responses) => {
                this.stringValues = responses[0];
                if (responses.length > 1) {
                    return this.refreshApprovedStatus(selectedBeginDate, selectedEndDate, !!data?.force);
                }
                return this._$q.when();
            })
            .finally(() => {
                this.isLoadingData = false;
                this.pickerDisabled = false;
            });
    }

    private isSingleDate(): boolean {
        return this.navigationMode === NavigationMode.Single;
    }

    /**
     * Click on update button in widget approval OR inside manual inputs widget
     * @param groupGuid Group guid
     * @param level Level to approve
     * @param status New status after approval
     * @param dateToApprove Date approved
     */
    private updateApprove(groupGuid: string, level: number, status: ApprovalStatus, dateToApprove: Date): angular.IPromise<void> {
        const group = this.groupsDictionary[groupGuid];
        const config = this.groupConfigs[groupGuid];
        const dataToApprove: DataToApprove = {
            groupGuid: groupGuid,
            groupStrategyGuid: group.approvalStrategyGuid,
            groupUniqueName: group.uniqueName,
            level: level,
            status: status,
            dateToApprove: dateToApprove,
            comment: undefined
        };
        const dateKey = moment(dateToApprove).format(momentFormat);
        const index = config?.approvalsByDate ? _.findIndex(config.approvalsByDate[dateKey], (approval) => approval.level === dataToApprove.level) : -1;
        // Case 1: Save data directly through global approval at widget level
        if (index === -1) {
            dataToApprove.endDateToApprove = this._validationStateService.getEndDateToApprove(dataToApprove, group, this.baseFrequency);
            return this.approveData([dataToApprove], true);
        } else {
            // Approval in manual input widget, save only when click on save button
            const approval = config.approvalsByDate[dateKey][index];
            config.approvalsByDate[dateKey][index] = this.getUpdatedApproval(approval, dataToApprove);
        }
        return this._$q.when();
    }

    /**
     * Approve data (API call)
     * @param dataToApproveList Data to approve
     * @param isJustification Is there a justification
     */
    private approveData(dataToApproveList: DataToApprove[], isJustification: boolean): angular.IPromise<void> {
        let promise: angular.IPromise<any>;
        if (isJustification && dataToApproveList && dataToApproveList.length === 1) {
            const dataToApprove = dataToApproveList[0];
            const justificationRequired = this._groupApprovalService.requiresJustification(
                dataToApprove.level,
                dataToApprove.status,
                this.approvalStrategies[dataToApprove.groupStrategyGuid]
            );
            if (justificationRequired) {
                promise = this.getApprovalJustification(
                    dataToApprove.status === ApprovalStatus.Rejected,
                    dataToApprove.groupUniqueName,
                    dataToApprove.dateToApprove.toISOString()
                )
                    .then((comment) => {
                        dataToApprove.comment = comment;
                        return this.saveDataToApprove([dataToApprove]);
                    })
                    .catch(() => {
                        // no comment was entered, don't do anything
                    });
            } else {
                dataToApprove.comment = undefined;
                promise = this.saveDataToApprove([dataToApprove]);
            }
        } else {
            promise = this.saveDataToApprove(dataToApproveList);
        }
        return (
            promise
                // convert to promise<void>
                .then(() => {
                    /* do nothing */
                })
        );
    }

    /**
     * Get update approval for UI only update (before saving through panel save button)
     * @param approval Approval to save
     * @param dataToApprove Data to approve
     */
    private getUpdatedApproval(approval: IApproval, dataToApprove: DataToApprove): IApproval {
        if (approval) {
            const isRequired = this._groupApprovalService.requiresJustification(
                dataToApprove.level,
                dataToApprove.status,
                this.approvalStrategies[dataToApprove.groupStrategyGuid]
            );
            if (isRequired) {
                this.getApprovalJustification(
                    dataToApprove.status === ApprovalStatus.Rejected,
                    dataToApprove.groupUniqueName,
                    dataToApprove.dateToApprove.toISOString()
                )
                    .then((comment) => {
                        dataToApprove.comment = comment;
                        this.applyJustifiedStatus(approval, dataToApprove);
                    })
                    .catch(() => {
                        // no comment was entered, don't do anything
                    });
            } else {
                this.applyJustifiedStatus(approval, dataToApprove);
            }
        } else {
            this._loggerService.logError('Approvals array not found');
        }
        return approval;
    }

    private applyJustifiedStatus(approval: IApproval, dataToApprove: DataToApprove) {
        // for UI display
        approval.status = dataToApprove.status;
        // for saving
        this.approvalsToSave.push(dataToApprove);
    }

    /**
     * Approves the data (API request), and refreshes afterwards.
     */
    private saveDataToApprove(dataToApproveList: DataToApprove[]): angular.IPromise<any> {
        return this._approvalManagementService.saveDataToApprove(dataToApproveList, this.groupsDictionary).then((data) => {
            const { dtoList, messages } = data;
            if (messages && messages.length) {
                const msg = messages.map((m) => this._$translate.instant(m)).join('\r\n');
                this._loggerService.logError(`${msg}`);
            } else {
                if (dtoList && dtoList.length > 0) {
                    const hasRejection = _.some(dtoList, (dto) => dto.status === -1);
                    const hasApproval = _.some(dtoList, (dto) => dto.status !== -1);
                    if (hasRejection) {
                        const msg = this._$translate.instant('Approval of group has been rejected');
                        this._loggerService.logSuccess(`${msg}`);
                    }
                    if (hasApproval) {
                        const msg = this._$translate.instant('Group approved');
                        this._loggerService.logSuccess(`${msg}`);
                    }
                }
                return this._$q.all([this.refreshApprovalTables(), this.refreshApprovedStatus(this.getBeginDate(), this.getEndDate())]);
            }
        });
    }

    getBeginDate(): Date {
        return this.isSingleDate() && this.selectedDate ? this.selectedDate : moment(this._dateRangeService.beginDate() as string).toDate();
    }

    getEndDate(): Date {
        return this.isSingleDate() ? moment(this.endDate)?.toDate() : moment(this._dateRangeService.endDate() as string).toDate();
    }

    getBegin(): string {
        return this.isSingleDate() && this.selectedDate ? this.selectedDate.toISOString() : moment(this._dateRangeService.beginDate() as string).toISOString();
    }

    getEnd(): string {
        return this.isSingleDate() ? moment(this.endDate)?.toISOString() : moment(this._dateRangeService.endDate() as string).toISOString();
    }

    /**
     * Refreshes the statuses of all widget buttons, then calls refreshLocalData.
     * @param force Force referesh (unused)
     */
    private refreshApprovedStatus(beginDate: Date, endDate: Date, force = true): angular.IPromise<void> {
        const validationWidgets: (IWidget & { configuration: IBindableWidgetConfiguration })[] = this.getValidationsWidgets();
        const manualInputWidgets: (IWidget & { configuration: IBindableWidgetConfiguration } & ManualInputsWidgetWithExtraProperties)[] =
            this.getManualWidgets();
        const editableWidgets = validationWidgets.concat(manualInputWidgets);
        const guidList = editableWidgets.map((w) => w.configuration.group).filter((guid) => guid);
        guidList.push(this.selectedGroup?.guid);
        const datesDic: Dictionary<Date[]> = {};
        const appFrequency = {
            interval: this.application.interval,
            intervalTimeUnit: this.application.intervalTimeUnit
        };
        manualInputWidgets.forEach((widget) => {
            if (guidList.includes(widget.configuration.group)) {
                const group = this.groupsDictionary[widget.configuration.group];
                const frequency = group?.frequency && group.frequency.interval > 0 ? group.frequency : appFrequency;
                const dates = this._manualInputsWidgetsService.getWidgetRange(widget, frequency, this.application, beginDate, endDate);
                datesDic[widget.configuration.group] = dates;
            } else {
                datesDic[widget.configuration.group] = [beginDate];
            }
        });
        validationWidgets.forEach((widget) => {
            if (guidList.includes(widget.configuration.group)) {
                const group = this.groupsDictionary[widget.configuration.group];
                const frequency = group?.frequency && group.frequency.interval > 0 ? group.frequency : appFrequency;
                const dates = this._manualInputsWidgetsService.getWidgetRange(
                    { configuration: { maxColumns: 1e6 } },
                    frequency,
                    this.application,
                    beginDate,
                    endDate
                );
                datesDic[widget.configuration.group] = dates;
            } else {
                datesDic[widget.configuration.group] = [beginDate];
            }
        });
        return this._approvalManagementService
            .getGroupApprovalStatuses(datesDic, guidList)
            .then((data) => {
                this.isGroupApproved = (Object as any).assign({}, data);
                const p = manualInputWidgets.map((w) => this.mapManualInputWidgets(w));
                return this._$q.all(p);
            })
            .then(() => this.refreshLocalData(force))
            .then(() => {
                // Check approvable status of manual inputs widgets entries by date
                const widgets: any[] = manualInputWidgets;
                widgets.forEach((widget) => {
                    const dateKeyList = [];
                    const isApprovable = {};
                    widget.dates.forEach((date, index) => {
                        // To be approvable, all values of a date must have data and not already be approved
                        const dateKey = moment(date).format(momentFormat);
                        isApprovable[dateKey] = _.every(
                            widget.inputList,
                            (input: TagManualInputValues) => input && input.values[index] && input.values[index].hasData
                        );
                        dateKeyList.push(dateKey);
                    });
                    widget.isApprovable = isApprovable;
                    widget.dateKeyList = dateKeyList;
                });
                return this.loadApprovalButtonsConfiguration();
            })
            .then((responses) => {
                this.groupConfigs = responses;
                this._pristineGroupsConfig = angular.copy(this.groupConfigs);
                if (this.selectedGroup?.guid in responses && this.share.approvalConfiguration) {
                    this.share.approvalConfiguration.approvals = responses[this.selectedGroup.guid].approvals;
                    this.share.approvalConfiguration.approvalsByDate = responses[this.selectedGroup.guid].approvalsByDate;
                }
            })
            .catch(() => {
                if (!this._hasDestroyed) {
                    this._loggerService.logError('Cannot check approved state for group, validation disabled');
                    this.isGroupApproved[this.selectedGroup.guid] = { isApproved: true };
                    validationWidgets.forEach((w) => (this.isGroupApproved[w.configuration.group] = { isApproved: true }));
                }
            })
            .finally(() => {
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    /**
     * Loads the approval buttons configuration for widgets that have the buttons enabled.
     */
    private loadApprovalButtonsConfiguration(): angular.IPromise<Dictionary<IApprovalButtonsConfiguration>> {
        // Legacy, don't know if still used
        const cfg = this.share.approvalConfiguration;
        cfg.timestamp = this.getBeginDate();

        // load dates for each group and get promises for approval buttons
        const datesByGroup = this.getDatesByGroupFromWidgets();
        const promisesByGroup = {};
        Object.keys(datesByGroup).forEach((guid) => {
            promisesByGroup[guid] = this._validationStateService.updateApprovalButtons(
                this.groupConfigs[guid],
                datesByGroup[guid].group,
                datesByGroup[guid].dates,
                this.getBegin(),
                this.getEnd()
            );
        });

        // resolve them by group
        return this._$q.all(promisesByGroup);
    }

    private getDatesByGroupFromWidgets(): Dictionary<{ dates: Date[]; group: Group }> {
        const result: Dictionary<{ dates: Date[]; group: Group }> = {};

        // Get configs for group defined in manual input widgets
        const approvableWidgets = this.widgetList.filter((w) => (w.configuration as any).enableApproval);
        approvableWidgets.forEach((w: ManualInputWidgetProperties & IWidget) => {
            const group = this.widgetGroups[w.guid];
            const workingDates = w.dates ? [...w.dates] : [this.getBeginDate()];

            if (result[group.guid]) {
                const concatened = result[group.guid].dates.concat(workingDates);
                result[group.guid].dates = _.uniqBy(concatened, (d) => d.toISOString());
            } else {
                result[group.guid] = { dates: workingDates, group: group };
            }
        });
        // Get config for main group
        if (this.selectedGroup?.guid && !result[this.selectedGroup.guid]) {
            result[this.selectedGroup.guid] = { dates: [this.getBeginDate()], group: this.selectedGroup };
        }

        return result;
    }

    /**
     * Refreshes approval tables widget after an approval.
     */
    private refreshApprovalTables(): angular.IPromise<void> {
        const approvalTables = this.widgetList.filter((w) => w.type === 'approvalTable');
        this.isLoadingData = true;
        return this._validationStateService
            .refreshAndAssign(
                approvalTables,
                this.getBegin(),
                this.getEnd(),
                this.selectedGroup.guid,
                this.digitalSets,
                this.stringValues,
                this.viewModels,
                true
            )
            .finally(() => {
                this.isLoadingData = false;
                this.pickerDisabled = false;
            });
    }

    //#region navigation
    gotoDestination(item: IWidget): void {
        if (item.dashboardLink && item.dashboardLink !== guidEmpty) {
            const isValidation = item.type === this.widgetName;
            const isManualInput = item.type === this.manualInputsWidgetType;
            let selectedGroupGuid = this.selectedGroup?.guid;
            if (isValidation || isManualInput) {
                const config = item.configuration as IValidatedValueSelectionConfiguration | ManualInputsWidgetConfiguration;
                if (config.group && config.group !== guidEmpty) {
                    selectedGroupGuid = config.group;
                }
            }
            const selectedGuid = this._dashboardHelperService.getSelectedGroupGuid(selectedGroupGuid);
            const beginDate = this.getBegin();
            const endDate = this.getEnd();
            const go = this._dashboardHelperService.getGotoDestination(item.dashboardLink, this.dashboards, selectedGuid, beginDate, endDate);
            this._$state.go(go.route, go.params);
        }
    }

    editWidget(widget: IWidget): void {
        const dates = this.getEditDates();
        this._$state.go(editGroupWidgetState, {
            groupGuid: this.selectedGroup.guid,
            widgetGuid: widget.guid,
            beginDate: dates.begin,
            endDate: dates.end
        });
    }

    editShapes(widget: IWidget): void {
        const dates = this.getEditDates();
        this._$state.go(editGroupWidgetShapeState, {
            groupGuid: this.selectedGroup.guid,
            widgetGuid: widget.guid,
            beginDate: dates.begin,
            endDate: dates.end
        });
    }

    goToApproval(item: ValidatedValueSelectionWidget): void {
        if (item) {
            let group = this.widgetGroups[item.guid];
            if (!group || group.approvalStrategyGuid === guidEmpty) {
                group = this.selectedGroup;
            }
            let strategy = this.approvalStrategies[group.approvalStrategyGuid];
            if (!strategy) {
                group = this.selectedGroup;
                strategy = this.approvalStrategies[group.approvalStrategyGuid];
            }
            if (!strategy) {
                // Find the first group with approval strategy as per #32399
                for (const key of Object.keys(this.widgetGroups)) {
                    group = this.widgetGroups[key];
                    strategy = this.approvalStrategies[group.approvalStrategyGuid];
                    if (strategy) {
                        break;
                    }
                }
            }
            const params = {
                groupGuid: group.guid,
                beginDate: this.getBeginDate().toISOString(),
                approvalStrategyGuid: strategy?.guid
            };
            this._$state.go('approval', params);
        }
        // return url;
    }

    private hasApprovalStrategy(item: IWidget): boolean {
        let hasApprovalStrategy = false;
        if (item) {
            let group = this.widgetGroups[item.guid];
            if (!group) {
                group = this.selectedGroup;
            }
            hasApprovalStrategy = group.approvalStrategyGuid !== guidEmpty;
        }
        return hasApprovalStrategy;
    }

    private updateHistoryName(name: string): void {
        const stateName = this._$translate.instant(this._$state.current.data.displayName);
        this._navigationHistoryService.updateLastHistory(`${stateName} - ${name}`, 'validation');
    }

    private saveRestoreState(list: IWidget[]): void {
        const layouts = {};
        list.forEach((item) => {
            const { name, sizeX, sizeY, col, row, backgroundColor, hideTitle, dashboardLink, isTitleHidden } = item;
            const l = { name, sizeX, sizeY, col, row, backgroundColor, hideTitle, dashboardLink, isTitleHidden } as IWidgetLayout;
            if (item.configuration) {
                l.configuration = Object.assign({}, item.configuration);
            }
            layouts[item.guid] = l;
        });
        this._pristineWidgets = layouts;
    }

    //#endregion

    onHover(item: IWidget): void {
        this.hover[item.guid] = true;
        this.computeShowButtons();
    }

    onMouseOut(item: IWidget): void {
        this.hover[item.guid] = false;
        this.computeShowButtons();
    }

    private computeShowButtons(): void {
        const result = {};

        this.widgetList.forEach((w) => {
            const show = this.share.formController?.$dirty && this.selectedItem ? this.selectedItem === w : this.hover[w.guid] || this.isEditMode;
            result[w.guid] = show && this.displayOverlay(w);
        });

        this.showButtons = result;
    }

    private computeLinkIcons(): void {
        this.widgetList.forEach((w) => {
            w.linkIcon = w.dashboardLink === guidGroupValidationPageLink ? 'fa-check-square-o' : 'fa-th-large';
        });
    }

    displayOverlay(item: any): boolean {
        const display: boolean =
            !!(
                item.isZoomEnabled ||
                item.isZoomable ||
                (item.isMaximizable && item.isMaximized && item.isDetachable !== false) ||
                item.baseType === 'badActors' ||
                (this.isEditMode && (item.canDuplicate === undefined || !!item.canDuplicate)) ||
                item.isExportable ||
                (this.isEditMode && (!item.canDelete || item.canDelete())) ||
                (this.isEditMode && item.hasExtendedProperties) ||
                (this.isEditMode && item.type === 'scatter') ||
                (!this.isEditMode && item.getError)
            ) ||
            item.error ||
            (item.dashboardLink && item.dashboardLink !== guidEmpty) ||
            item.type !== this.manualInputsWidgetType;
        return display;
    }

    toggleSelection(item: IZoomableWithLogbookSelectionWidget): void {
        item.isSelectingLogbook = !item.isSelectingLogbook;
        item.select();
    }

    openModal(item: LogbookTableWidget): void {
        const series = (item.configuration.series || []).map<ILogbookModalSeries>((s) => ({ isBoundToGroup: s.isBoundToGroup, vm: this.viewModels[s.guid] }));
        this._logbookService.openModal(
            undefined,
            { severity: 0 },
            false,
            this.hasManualInputRoles,
            true,
            { startDate: this.getBegin(), endDate: this.getEnd() },
            undefined,
            undefined,
            item.prefilters,
            item.allowedGroupGuidList,
            series,
            item.configuration.taskOptionsOverride,
            item.configuration.launchTask
        );
    }

    private disableLogbookSelection(): void {
        if (this.isEditMode) {
            this.widgetList
                .filter((w: IWidget & ILogbookSelection) => w.isSelectingLogbook)
                .forEach((w: IWidget & ILogbookSelection & IZoomControl) => this.toggleSelection(w));
        }
    }

    //#region spot values
    onSaveSpotValuesStart(): void {
        this.loadingMessageText = 'Saving spot values';
        this.isSavingSpotValues = true;
    }

    onSaveSpotValuesComplete(): void {
        this.loadingMessageText = null;
        this.isSavingSpotValues = false;
    }

    onSaveSpotValues(): void {
        this.isLoading = true;
        this.readSpotValues(true)
            .then((data) => {
                this.spotValues = data;
            })
            .finally(() => {
                this.isLoading = false;
                this.pickerDisabled = false;
            });
    }

    /**
     * Reads spot values; also initializes series per widget. Could be optimized, some operations could be done once at widget load/update, and not on every refresh.
     * @param force Force refresh of data.
     * @returns The mapped data dictionary.
     */
    private readSpotValues(force: boolean = false): angular.IPromise<{ [widgetGuid: string]: EditSpotValueWithOptions[] }> {
        const widgets = this.getSpotManualWidgets();
        try {
            if (widgets.length) {
                const withLastTags = [].concat(
                    ...widgets
                        .filter((w) => w.configuration.hasLastValue)
                        .map((w) => {
                            const groupGuid = this.widgetGroups[w.guid]?.guid || this.selectedGroup.guid;
                            return this.spotManualTagsPerGroup[groupGuid] || [];
                        })
                );
                const withoutLastTags = [].concat(
                    ...widgets
                        .filter((w) => !w.configuration.hasLastValue)
                        .map((w) => {
                            const groupGuid = this.widgetGroups[w.guid]?.guid || this.selectedGroup.guid;
                            return this.spotManualTagsPerGroup[groupGuid] || [];
                        })
                );

                const promises: angular.IPromise<SpotValue[]>[] = [];
                const bd = this.getBeginDate().toISOString();
                const ed = this.getEndDate().toISOString();
                if (withLastTags.length) {
                    promises.push(
                        this._spotValuesService.getBetweenDatesWithLastBefore(
                            withLastTags.map((d) => d.guid),
                            bd,
                            ed,
                            force
                        )
                    );
                }
                if (withoutLastTags.length) {
                    promises.push(
                        this._spotValuesService.getBetweenDatesForList(
                            withoutLastTags.map((d) => d.guid),
                            bd,
                            ed,
                            force
                        )
                    );
                }
                if (promises.length) {
                    return this._$q.all(promises).then((data) => {
                        const tagDic: Dictionary<TagDefinition> = {};
                        withoutLastTags.forEach((t) => {
                            tagDic[t.guid] = t;
                        });
                        withLastTags.forEach((t) => {
                            tagDic[t.guid] = t;
                        });
                        const allSpotValues: SpotValue[] = [].concat(...data);
                        const withOptions = this._spotValuesHelperService.mapSpotValuesToEditSpotValuesWithOptions(
                            allSpotValues,
                            this.digitalSetDictionary,
                            tagDic,
                            this.viewModels,
                            (_sv, _i) => {
                                /* do nothing */
                            }
                        );
                        const dataDic = {};
                        const tagsPerWidget = {};
                        widgets.forEach((w) => {
                            try {
                                const groupGuid = this.widgetGroups[w.guid]?.guid || this.selectedGroup.guid;
                                const tagsPerGroup = this.spotManualTagsPerGroup[groupGuid];
                                if (tagsPerGroup && tagsPerGroup.length) {
                                    const ctg = tagsPerGroup.map((d) => d.guid);
                                    tagsPerWidget[w.guid] = ctg.map((d) => tagDic[d]);
                                    dataDic[w.guid] = withOptions
                                        .filter((wo) => ctg.indexOf(wo.tagGuid) >= 0)
                                        .map((smiw) => {
                                            let identifier = smiw.identifier;
                                            if (!identifier) {
                                                const identifiers = this.groupsDictionary[groupGuid]?.identifierCollection
                                                    ?.filter((i) => i.tagDefinitionGuid === smiw.tagGuid)
                                                    .sort((a, b) => (a.sortingOrder > b.sortingOrder ? 1 : -1));
                                                identifier = identifiers.length ? (this.viewModels[identifiers[0].guid] as IIdentifierVm) : null;
                                            }
                                            return Object.assign({}, smiw, { identifier });
                                        })
                                        .sort((a, b) => {
                                            if (a.timestamp < b.timestamp) {
                                                return -1;
                                            } else if (b.timestamp < a.timestamp) {
                                                return 1;
                                            } else {
                                                return a.tagDefinition?.name > b.tagDefinition?.name ? 1 : -1;
                                            }
                                        });
                                }
                            } catch (e) {
                                console.error(e);
                            }
                        });
                        this.spotManualTagsPerWidget = tagsPerWidget;
                        return dataDic;
                    });
                } else {
                    this.spotManualTagsPerWidget = {};
                    return this._$q.when({});
                }
            } else {
                return this._$q.when({});
            }
        } catch (error) {
            console.error(error);
        }
    }
    //#endregion
}

export const wtValidationState: ComponentDefinition = {
    componentId: 'wtValidationState',
    controller: ValidationStateController,
    template: require('./validationState.component.html'),
    controllerAs: 'vm',
    bindings: {
        currentInterval: '=?',
        //for stand alone page
        date: '=?',
        hasApprobation: '=?',
        interval: '=?',
        miniatureList: '=?',
        navigationMode: '<',
        pickerDisabled: '=?',
        selectedGroup: '<',
        selectedItem: '=',
        share: '<',
        title: '=?',
        widgetList: '=?' // to push widgets to parent component
    }
};
